/**
 * SegmentedControl — pick one of a few options, shown as one joined strip (Product, Platform, Layout, Show, …).
 * Use it for a setting or a view with 2–5 short choices. For filtering a list with counts use FilterChips; for pages use Tabs.
 */
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

export interface SegmentedItem<T extends string> {
  id: T;
  label: ReactNode;
  icon?: LucideIcon;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  items: readonly SegmentedItem<T>[];
  /** Spoken name of the group. Use `labelledBy` instead when a visible label already says it. */
  label?: string;
  labelledBy?: string;
  disabled?: boolean;
  /** Stretch to the width of the column, each choice taking an equal share. */
  fullWidth?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string>({ value, onChange, items, label, labelledBy, disabled, fullWidth, className }: SegmentedControlProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} aria-labelledby={labelledBy} className={cn("max-w-full flex-wrap gap-0.5 rounded-lg bg-neutral-100 p-0.5", fullWidth ? "flex w-full" : "inline-flex", className)}>
      {items.map((it) => {
        const on = value === it.id;
        return (
          <button
            key={it.id}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(it.id)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-base font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
              fullWidth && "flex-1 justify-center",
              on ? "bg-white text-neutral-900 shadow-xs" : "text-neutral-500 hover:text-neutral-800"
            )}
          >
            {it.icon && <it.icon className="size-4" aria-hidden />}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
